import { useEffect, useMemo, useRef, useState } from 'react';

import TopBar from '../components/TopBar';
import { useStore } from '../store';
import { AsrErrorCode, isSupported as asrSupported, listen } from '../asr';
import { normalize } from '../utils';

export default function DictationScreen() {
  const s = useStore();
  const lesson = s.currentLesson;
  const item = s.currentItem;
  const [showMeaning, setShowMeaning] = useState(false);
  const [result, setResult] = useState<boolean | null>(null);
  const [listening, setListening] = useState(false);
  const [micError, setMicError] = useState<AsrErrorCode | null>(null);
  const cancelListenRef = useRef<(() => void) | null>(null);

  // Firefox has no SpeechRecognition, so the button is never rendered there
  // rather than failing when pressed.
  const micAvailable = useMemo(() => asrSupported(), []);

  // Auto-play the sentence when entering or moving to a new card.
  useEffect(() => {
    setShowMeaning(false);
    setResult(null);
    setMicError(null);
    s.playCurrent();
    return () => s.stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.currentIndex]);

  // Never leave the microphone open behind us.
  useEffect(() => () => cancelListenRef.current?.(), []);

  if (!lesson || !item) return null;
  const total = lesson.items.length;
  const answer = item.eng_text;

  const check = () => setResult(s.checkDictation());

  const stopListening = () => {
    cancelListenRef.current?.();
    cancelListenRef.current = null;
    setListening(false);
  };

  const startListening = () => {
    if (listening) {
      stopListening();
      return;
    }
    // Self-hearing guard: the screen auto-plays the sentence, and an open mic
    // would happily transcribe our own synthesised voice and mark it correct.
    s.stopPlayback();
    setMicError(null);
    setResult(null);

    cancelListenRef.current = listen({
      onStart: () => setListening(true),
      onResult: (alternatives) => {
        // Accept any ranked alternative that matches; the engine frequently
        // mis-ranks short phrases. Show the matching one so the user sees
        // what was accepted, otherwise show its best guess.
        const target = normalize(answer);
        const matched = alternatives.find((alt) => normalize(alt) === target);
        s.setDictationInput(matched ?? alternatives[0]);
        setResult(matched !== undefined);
      },
      onError: (code) => setMicError(code),
      onEnd: () => {
        cancelListenRef.current = null;
        setListening(false);
      },
    });
  };

  const micErrorText = (code: AsrErrorCode): string => {
    switch (code) {
      case 'not-allowed':
        return s.t('dictation.mic_denied');
      case 'no-speech':
        return s.t('dictation.mic_no_speech');
      case 'audio-capture':
        return s.t('dictation.mic_no_device');
      case 'network':
        return s.t('dictation.mic_network');
      default:
        return s.t('dictation.mic_failed');
    }
  };

  return (
    <div className="screen">
      <TopBar
        title={s.t('mode.dictation.title')}
        subtitle={lesson.unit.trim()}
        onBack={s.backToScenarioMenu}
        backLabel={s.t('nav.back')}
      />
      <div className="scroll">
        <div className="body">
          <div className="counter" style={{ textAlign: 'center', display: 'block', marginBottom: '0.75rem' }}>
            {s.currentIndex + 1} / {total}
          </div>

          <div className="hint-card">
            <div className="hint-card__head">
              <span className="hint-card__label">{s.t('dictation.meaning_label')}</span>
              <button
                type="button"
                className="hint-card__toggle"
                onClick={() => setShowMeaning((m) => !m)}
                aria-expanded={showMeaning}
              >
                {showMeaning ? s.t('dictation.hide_meaning') : s.t('dictation.show_meaning')}
              </button>
            </div>
            {showMeaning && <div className="hint-card__value">{item.kor_text || '—'}</div>}
          </div>

          <button
            type="button"
            className="btn btn--primary btn--block"
            style={{ margin: '0.9rem 0' }}
            onClick={s.replay}
          >
            {s.t('dictation.replay_button')}
          </button>

          <label className="field-label" htmlFor="dictation-input">
            {s.t('dictation.input_label')}
          </label>
          <textarea
            id="dictation-input"
            className="textarea"
            value={s.dictationInput}
            onChange={(e) => s.setDictationInput(e.target.value)}
            onKeyDown={(e) => {
              // Enter checks; Shift+Enter inserts a newline.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                check();
              }
            }}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />

          {micAvailable && (
            <>
              <button
                type="button"
                className={`btn btn--outline btn--block mic-btn${listening ? ' is-listening' : ''}`}
                style={{ marginTop: '0.6rem' }}
                onClick={startListening}
                // Speaking and listening at once would let the app hear itself.
                disabled={s.isSpeaking}
                aria-pressed={listening}
              >
                {listening ? s.t('dictation.listening') : s.t('dictation.speak_button')}
              </button>
              {micError && (
                <p className="mic-error" role="alert">
                  {micErrorText(micError)}
                </p>
              )}
              <p className="hint">{s.t('dictation.mic_hint')}</p>
            </>
          )}

          {result !== null && (
            <div className={`result ${result ? 'result--ok' : 'result--bad'}`} role="status">
              <p className="result__title">
                {result ? s.t('dictation.correct') : s.t('dictation.incorrect')}
              </p>
              <p className="result__answer">
                {s.t('dictation.answer_prefix', { answer: item.eng_text })}
              </p>
            </div>
          )}

          <button
            type="button"
            className="btn btn--primary btn--block"
            style={{ marginTop: '0.9rem' }}
            onClick={check}
          >
            {s.t('dictation.check_button')}
          </button>

          <div className="nav-row">
            <button
              type="button"
              className="btn btn--outline"
              disabled={s.currentIndex === 0}
              onClick={() => s.prev(false)}
            >
              {s.t('nav.prev')}
            </button>
            <button
              type="button"
              className="btn btn--outline"
              disabled={s.currentIndex >= total - 1}
              onClick={() => s.next(false)}
            >
              {s.t('nav.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
